import { useTranslation } from 'react-i18next'

import { usePriceRules } from '../../../../../hooks/pricing/usePriceRules'

import Icon from '../../../../ui/Icon'
import { DropdownMenu } from '../../../../dropdownMenu/DropdownMenu'
import { PriceRuleVersions } from './priceRuleVersions/PriceRuleVersions'
import { PriceRuleStatusBadge, usePriceRuleFormat } from './priceRuleFormat'
import { RulePrices } from './priceRuleParts'
import {
  HistoryToggle,
  ScheduleCourts,
  ScheduleDays,
  ScheduleTimeline,
  useVersionHistory,
} from '../../scheduleCard/scheduleParts'

import { PriceRule } from '../../../../../types/booking/booking'
import { ActionType } from '../../../../../constants/actionTypes'

type PriceRuleCardProps = {
  rule: PriceRule
  courtNames: Map<number | undefined, string>
  /** Club opening hours, the span of the timeline. */
  openTime?: string
  closeTime?: string
  onAction: (rule: PriceRule, action: ActionType) => void
}

/**
 * One price rule (its active version). Older versions are fetched for this rule only, the first time the history is expanded.
 */
export const PriceRuleCard = ({ rule, courtNames, openTime, closeTime, onAction }: PriceRuleCardProps) => {
  const { t } = useTranslation('pricing')
  const { versions } = usePriceRules()
  const format = usePriceRuleFormat()
  const history = useVersionHistory(() => versions(rule.id!))

  const hasHistory = (rule.version ?? 1) > 1

  return (
    <div className="ph-card schedule-card">
      <span className="schedule-card-stripe" aria-hidden="true" />

      <div className="schedule-card-content">
        <div className="schedule-card-header">
          <div className="schedule-card-title">
            <h3>{rule.name}</h3>
            <span className="schedule-card-meta mono">{t('pricing.manager.version', { version: rule.version })}</span>
            <span className="schedule-card-meta">{format.period(rule)}</span>
            {hasHistory && (
              <HistoryToggle
                open={history.open}
                label={t(history.open ? 'pricing.card.hide-history' : 'pricing.card.show-history')}
                onToggle={history.toggle}
              />
            )}
          </div>
          <div className="schedule-card-corner">
            <PriceRuleStatusBadge status={rule.status} />
            <DropdownMenu
              label={t('pricing.card.more')}
              items={[
                {
                  key: 'edit',
                  label: t('pricing.manager.edit'),
                  icon: 'edit',
                  onClick: () => onAction(rule, ActionType.EDIT),
                },
                {
                  key: 'archive',
                  label: t('pricing.manager.archive'),
                  icon: 'trash',
                  danger: true,
                  onClick: () => onAction(rule, ActionType.DELETE),
                },
              ]}
            />
          </div>
        </div>

        <div className="schedule-card-facts">
          <div className="schedule-card-fact">
            <span className="schedule-card-label">
              <Icon name="clock" size={14} />
              {t('pricing.card.start-times')}
            </span>
            <span className="mono schedule-card-times">{format.startTimes(rule)}</span>
          </div>
          <div className="schedule-card-fact is-days">
            <span className="schedule-card-label">
              <Icon name="calendar" size={14} />
              {t('pricing.card.days')}
            </span>
            <ScheduleDays weekdays={rule.weekdays} />
          </div>
          <div className="schedule-card-fact is-courts">
            <span className="schedule-card-label">
              <Icon name="court" size={14} />
              {t('pricing.card.courts')}
            </span>
            <ScheduleCourts courtIds={rule.courtIds} courtNames={courtNames} />
          </div>
          <div className="schedule-card-fact is-prices">
            <span className="schedule-card-label">
              <Icon name="tag" size={14} />
              {t('pricing.card.pricing')}
            </span>
            <RulePrices rule={rule} />
          </div>
        </div>

        <ScheduleTimeline intervals={[rule.interval]} openTime={openTime} closeTime={closeTime} />

        {history.open && <PriceRuleVersions history={history.history} courtNames={courtNames} />}
      </div>
    </div>
  )
}
